import { useEffect, useRef, useState } from "react";
import { a3Copy } from "@/components/assignments/a3-copy";
import { chooseGoogleDriveFile, type GoogleDriveSelection } from "@/lib/google-drive-picker";
import { googleDrivePickerConfigForHostname } from "@/lib/google-drive-picker-config";

/**
 * A learner's own Drive as a link source (owner decision D2). The chooser opens
 * on My Drive only: a learner has no course folder, and IMAM stores the chosen
 * file's link as a reference, never a copy. Google keeps deciding who can open it.
 */
export function useHandInDriveChooser(active: boolean) {
  const config = googleDrivePickerConfigForHostname();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocus = useRef(false);
  const request = useRef<AbortController | null>(null);

  useEffect(() => () => { request.current?.abort(); request.current = null; }, []);
  useEffect(() => {
    if (busy || !restoreFocus.current) return;
    restoreFocus.current = false;
    triggerRef.current?.focus();
  }, [busy]);

  /** The chosen file, or null when the learner closed the chooser or Google failed. */
  const choose = async (): Promise<GoogleDriveSelection | null> => {
    if (!active || !config.enabled || request.current) return null;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true); setError(null);
    try {
      const outcome = await chooseGoogleDriveFile({ ...config, parentFolderId: null }, undefined, controller.signal);
      if (controller.signal.aborted) return null;
      if (outcome.kind === "picked") return outcome.selection;
      if (outcome.kind !== "cancelled") setError(a3Copy.googleDriveIsUnavailablePasteYourLinkInstead);
      return null;
    } catch {
      setError(a3Copy.googleDriveIsUnavailablePasteYourLinkInstead);
      return null;
    } finally {
      if (request.current === controller) { request.current = null; restoreFocus.current = true; setBusy(false); }
    }
  };

  return { enabled: active && config.enabled, busy, error, triggerRef, choose, cancel: () => request.current?.abort(), clearError: () => setError(null) };
}
